/**
 * CoachPlaybook - Data Management Component
 * Version: 0.6.0
 * Description: Broadcast-quality dark theme UI for backup, export, and data management with IndexedDB
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  exportAllData,
  importData,
  getStorageStats,
  getBackupStatus,
  clearAllData
} from '../../utils/db';
import { useSport } from '../common';

const DataManagement = () => {
  const { sportConfig } = useSport();
  const [backupStatus, setBackupStatus] = useState(null);
  const [storageStats, setStorageStats] = useState(null);
  const [message, setMessage] = useState(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [infoExpanded, setInfoExpanded] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    const backup = await getBackupStatus();
    const storage = await getStorageStats();
    setBackupStatus(backup);
    setStorageStats(storage);
  };

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleExportAll = async () => {
    const result = await exportAllData();
    if (result.success) {
      showMessage(`Exported all data to ${result.fileName}`, 'success');
      loadStatus();
    } else {
      showMessage(`Export failed: ${result.error}`, 'error');
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const result = await importData(file);
      showMessage(
        `Imported: ${result.itemsImported.players} players, ${result.itemsImported.plays} plays, ${result.itemsImported.opponents} opponents`,
        'success'
      );
      loadStatus();
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      showMessage(`Import failed: ${error.error}`, 'error');
    }

    event.target.value = '';
  };

  const handleClearData = async () => {
    const result = await clearAllData();
    if (result.success) {
      showMessage('All data cleared', 'success');
      setShowClearConfirm(false);
      setTimeout(() => window.location.reload(), 1500);
    } else {
      showMessage(`Clear failed: ${result.error}`, 'error');
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  const getDaysSinceColor = (days) => {
    if (days === null || days === undefined) return '#ef4444';
    if (days < 7) return '#34d399';
    if (days <= 14) return '#fbbf24';
    return '#ef4444';
  };

  const getDaysSinceLabel = (days) => {
    if (days === null || days === undefined) return 'Never backed up';
    if (days === 0) return 'Today';
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  const totalSize = storageStats?.sizes?.total || 0;
  const maxBarSize = Math.max(
    storageStats?.sizes?.players || 0,
    storageStats?.sizes?.plays || 0,
    storageStats?.sizes?.opponents || 0,
    1
  );

  /* ── SVG Icons ── */
  const CloudIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 16.2A4.5 4.5 0 0017.5 8h-1.8A7 7 0 104 14.9" />
      <path d="M12 12v9" />
      <path d="M16 16l-4-4-4 4" />
    </svg>
  );

  const DownloadIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );

  const UploadIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );

  const TrashIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
    </svg>
  );

  const WarningIcon = () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );

  const PlayersIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87" />
      <path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  );

  const PlaysIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );

  const OpponentsIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fb923c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
    </svg>
  );

  const StorageIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    </svg>
  );

  const ChevronIcon = ({ expanded }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.25s ease' }}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );

  /* ── Styles ── */
  const s = {
    page: {
      fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif",
      background: '#0a0e17',
      minHeight: '100vh',
      padding: '24px',
      color: '#e2e8f0',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      marginBottom: '28px',
    },
    headerIcon: {
      width: '48px',
      height: '48px',
      borderRadius: '14px',
      background: 'linear-gradient(135deg, rgba(56,189,248,0.15), rgba(167,139,250,0.15))',
      border: '1px solid rgba(56,189,248,0.2)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    headerTitle: {
      fontFamily: "'IBM Plex Sans', sans-serif",
      fontSize: '26px',
      fontWeight: 700,
      color: '#f1f5f9',
      margin: 0,
      letterSpacing: '-0.02em',
    },
    headerSub: {
      fontFamily: "'IBM Plex Sans', sans-serif",
      fontSize: '13px',
      color: '#64748b',
      margin: 0,
    },
    sportBadge: {
      marginLeft: 'auto',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      background: 'rgba(56,189,248,0.08)',
      border: '1px solid rgba(56,189,248,0.15)',
      borderRadius: '20px',
      padding: '5px 14px',
      fontSize: '13px',
      color: '#94a3b8',
      fontFamily: "'IBM Plex Sans', sans-serif",
    },
    sportIcon: {
      fontSize: '16px',
    },

    /* Toast */
    toast: (type) => ({
      padding: '12px 16px',
      marginBottom: '20px',
      borderRadius: '10px',
      background: '#0f172a',
      border: '1px solid #1e293b',
      borderLeft: `4px solid ${type === 'success' ? '#34d399' : type === 'error' ? '#ef4444' : '#fbbf24'}`,
      fontSize: '14px',
      fontFamily: "'IBM Plex Sans', sans-serif",
      color: type === 'success' ? '#6ee7b7' : type === 'error' ? '#fca5a5' : '#fde68a',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    }),

    /* Glass card */
    card: {
      background: 'linear-gradient(135deg, rgba(15,23,42,0.9), rgba(30,41,59,0.6))',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      border: '1px solid #334155',
      borderRadius: '14px',
      padding: '20px',
      marginBottom: '20px',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    },
    cardHover: {
      borderColor: 'rgba(56,189,248,0.3)',
      boxShadow: '0 0 20px rgba(56,189,248,0.06)',
    },

    /* Backup Status */
    backupCard: {
      background: 'linear-gradient(135deg, rgba(15,23,42,0.95), rgba(30,41,59,0.7))',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      border: '1px solid #334155',
      borderRadius: '14px',
      padding: '24px',
      marginBottom: '20px',
    },
    backupHeader: {
      fontSize: '15px',
      fontWeight: 600,
      color: '#94a3b8',
      marginBottom: '16px',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      fontFamily: "'IBM Plex Sans', sans-serif",
    },
    backupRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 0',
    },
    backupLabel: {
      fontSize: '14px',
      color: '#64748b',
      fontFamily: "'IBM Plex Sans', sans-serif",
    },
    backupValue: (color) => ({
      fontSize: '14px',
      fontWeight: 600,
      color: color || '#e2e8f0',
      fontFamily: "'IBM Plex Mono', monospace",
    }),
    backupNowBtn: {
      marginTop: '16px',
      width: '100%',
      padding: '14px',
      borderRadius: '10px',
      border: 'none',
      background: 'linear-gradient(135deg, #0ea5e9, #38bdf8)',
      color: '#0c1220',
      fontSize: '15px',
      fontWeight: 700,
      fontFamily: "'IBM Plex Sans', sans-serif",
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      letterSpacing: '0.02em',
    },

    /* Stats grid */
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '14px',
      marginBottom: '20px',
    },
    statCard: (accentColor) => ({
      background: 'linear-gradient(135deg, rgba(15,23,42,0.9), rgba(30,41,59,0.5))',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      border: '1px solid #334155',
      borderRadius: '12px',
      padding: '16px',
      transition: 'border-color 0.2s ease',
      position: 'relative',
      overflow: 'hidden',
    }),
    statCardGlow: (accentColor) => ({
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '2px',
      background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)`,
      opacity: 0.6,
    }),
    statIconRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '8px',
    },
    statLabel: {
      fontSize: '12px',
      color: '#64748b',
      fontFamily: "'IBM Plex Sans', sans-serif",
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
    },
    statNumber: (color) => ({
      fontSize: '28px',
      fontWeight: 700,
      color: color || '#f1f5f9',
      fontFamily: "'IBM Plex Mono', monospace",
      lineHeight: 1.1,
      marginBottom: '4px',
    }),
    statSize: {
      fontSize: '12px',
      color: '#475569',
      fontFamily: "'IBM Plex Mono', monospace",
    },
    progressBarBg: {
      height: '4px',
      borderRadius: '2px',
      background: 'rgba(51,65,85,0.5)',
      marginTop: '10px',
      overflow: 'hidden',
    },
    progressBarFill: (pct, color) => ({
      height: '100%',
      borderRadius: '2px',
      width: `${Math.max(pct, 3)}%`,
      background: `linear-gradient(90deg, ${color}, ${color}88)`,
      transition: 'width 0.4s ease',
    }),

    /* Actions */
    actionsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '14px',
      marginBottom: '20px',
    },
    actionCard: {
      background: 'linear-gradient(135deg, rgba(15,23,42,0.9), rgba(30,41,59,0.5))',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      border: '1px solid #334155',
      borderRadius: '12px',
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      transition: 'border-color 0.2s ease, transform 0.15s ease',
    },
    actionIconWrap: (bg) => ({
      width: '44px',
      height: '44px',
      borderRadius: '12px',
      background: bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '12px',
    }),
    actionTitle: {
      fontSize: '15px',
      fontWeight: 600,
      color: '#e2e8f0',
      fontFamily: "'IBM Plex Sans', sans-serif",
      marginBottom: '4px',
    },
    actionDesc: {
      fontSize: '12px',
      color: '#64748b',
      fontFamily: "'IBM Plex Sans', sans-serif",
      marginBottom: '16px',
      lineHeight: 1.4,
    },
    btnAccent: {
      width: '100%',
      padding: '10px',
      borderRadius: '8px',
      border: 'none',
      background: 'linear-gradient(135deg, #0ea5e9, #38bdf8)',
      color: '#0c1220',
      fontSize: '13px',
      fontWeight: 700,
      fontFamily: "'IBM Plex Sans', sans-serif",
      cursor: 'pointer',
      transition: 'opacity 0.15s ease',
      marginTop: 'auto',
    },
    btnSecondary: {
      width: '100%',
      padding: '10px',
      borderRadius: '8px',
      border: '1px solid #475569',
      background: 'transparent',
      color: '#a78bfa',
      fontSize: '13px',
      fontWeight: 600,
      fontFamily: "'IBM Plex Sans', sans-serif",
      cursor: 'pointer',
      transition: 'background 0.15s ease',
      marginTop: 'auto',
    },
    btnDanger: {
      width: '100%',
      padding: '10px',
      borderRadius: '8px',
      border: 'none',
      background: 'linear-gradient(135deg, #dc2626, #ef4444)',
      color: '#fff',
      fontSize: '13px',
      fontWeight: 700,
      fontFamily: "'IBM Plex Sans', sans-serif",
      cursor: 'pointer',
      transition: 'opacity 0.15s ease',
      marginTop: 'auto',
    },

    /* Info collapsible */
    infoHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      padding: '14px 18px',
      background: 'linear-gradient(135deg, rgba(15,23,42,0.9), rgba(30,41,59,0.5))',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      border: '1px solid #334155',
      borderRadius: '12px',
      marginBottom: '0',
      transition: 'border-color 0.2s ease',
    },
    infoTitle: {
      fontSize: '14px',
      fontWeight: 600,
      color: '#94a3b8',
      fontFamily: "'IBM Plex Sans', sans-serif",
    },
    infoBody: {
      background: 'rgba(15,23,42,0.7)',
      border: '1px solid #334155',
      borderTop: 'none',
      borderRadius: '0 0 12px 12px',
      padding: '16px 18px',
    },
    infoItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '8px',
      marginBottom: '10px',
      fontSize: '13px',
      color: '#94a3b8',
      fontFamily: "'IBM Plex Sans', sans-serif",
      lineHeight: 1.5,
    },
    infoBullet: {
      color: '#38bdf8',
      fontWeight: 700,
      flexShrink: 0,
    },
    infoStrong: {
      color: '#cbd5e1',
      fontWeight: 600,
    },

    /* Modal */
    overlay: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.7)',
      backdropFilter: 'blur(4px)',
      WebkitBackdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '16px',
    },
    modal: {
      background: '#0f172a',
      border: '1px solid #334155',
      borderRadius: '16px',
      padding: '28px',
      maxWidth: '420px',
      width: '100%',
      boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
    },
    modalWarning: {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: '16px',
    },
    modalTitle: {
      fontSize: '20px',
      fontWeight: 700,
      color: '#fbbf24',
      fontFamily: "'IBM Plex Sans', sans-serif",
      textAlign: 'center',
      marginBottom: '12px',
    },
    modalText: {
      fontSize: '14px',
      color: '#94a3b8',
      fontFamily: "'IBM Plex Sans', sans-serif",
      textAlign: 'center',
      marginBottom: '16px',
      lineHeight: 1.5,
    },
    modalList: {
      listStyle: 'none',
      padding: 0,
      margin: '0 0 16px 0',
    },
    modalListItem: {
      fontSize: '13px',
      color: '#64748b',
      fontFamily: "'IBM Plex Sans', sans-serif",
      padding: '4px 0',
      paddingLeft: '16px',
      position: 'relative',
    },
    modalListBullet: {
      position: 'absolute',
      left: 0,
      color: '#ef4444',
    },
    modalDanger: {
      fontSize: '14px',
      fontWeight: 700,
      color: '#ef4444',
      fontFamily: "'IBM Plex Sans', sans-serif",
      textAlign: 'center',
      marginBottom: '20px',
    },
    modalBtns: {
      display: 'flex',
      gap: '12px',
    },
    modalCancel: {
      flex: 1,
      padding: '12px',
      borderRadius: '10px',
      border: '1px solid #475569',
      background: 'transparent',
      color: '#e2e8f0',
      fontSize: '14px',
      fontWeight: 600,
      fontFamily: "'IBM Plex Sans', sans-serif",
      cursor: 'pointer',
      transition: 'background 0.15s ease',
    },
    modalDelete: {
      flex: 1,
      padding: '12px',
      borderRadius: '10px',
      border: 'none',
      background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
      color: '#fff',
      fontSize: '14px',
      fontWeight: 700,
      fontFamily: "'IBM Plex Sans', sans-serif",
      cursor: 'pointer',
      transition: 'opacity 0.15s ease',
    },

    /* Responsive: stack actions on small screens */
    '@media (max-width: 640px)': {
      actionsGrid: {
        gridTemplateColumns: '1fr',
      },
    },
  };

  /* ── Responsive actions grid ── */
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const actionsGridStyle = isMobile
    ? { ...s.actionsGrid, gridTemplateColumns: '1fr' }
    : s.actionsGrid;

  const statsGridStyle = isMobile
    ? { ...s.statsGrid, gridTemplateColumns: '1fr' }
    : s.statsGrid;

  return (
    <div style={s.page}>
      {/* ── Header ── */}
      <div style={s.header}>
        <div style={s.headerIcon}>
          <CloudIcon />
        </div>
        <div>
          <h1 style={s.headerTitle}>Data Management</h1>
          <p style={s.headerSub}>Backup, export, and manage your coaching data</p>
        </div>
        <div style={s.sportBadge}>
          <span style={s.sportIcon}>{sportConfig?.icon || ''}</span>
          <span>{sportConfig?.name || 'Sport'}</span>
        </div>
      </div>

      {/* ── Toast Message ── */}
      {message && (
        <div style={s.toast(message.type)}>
          <span style={{ fontSize: '16px' }}>
            {message.type === 'success' ? '\u2713' : message.type === 'error' ? '\u2717' : '\u26A0'}
          </span>
          {message.text}
        </div>
      )}

      {/* ── Backup Status Card ── */}
      {backupStatus && (
        <div style={s.backupCard}>
          <div style={s.backupHeader}>Backup Status</div>
          <div style={s.backupRow}>
            <span style={s.backupLabel}>Last Backup</span>
            <span style={s.backupValue(
              backupStatus.hasBackup ? '#e2e8f0' : '#ef4444'
            )}>
              {backupStatus.hasBackup ? backupStatus.lastBackupDate : 'Never'}
            </span>
          </div>
          <div style={s.backupRow}>
            <span style={s.backupLabel}>Days Since Backup</span>
            <span style={s.backupValue(
              getDaysSinceColor(backupStatus.hasBackup ? backupStatus.daysSinceBackup : null)
            )}>
              {getDaysSinceLabel(backupStatus.hasBackup ? backupStatus.daysSinceBackup : null)}
            </span>
          </div>
          <div style={s.backupRow}>
            <span style={s.backupLabel}>Recommendation</span>
            <span style={s.backupValue(
              backupStatus.recommendation === 'Backup recommended' ? '#fbbf24' : '#34d399'
            )}>
              {backupStatus.recommendation}
            </span>
          </div>
          <button
            style={s.backupNowBtn}
            onClick={handleExportAll}
            onMouseEnter={(e) => { e.target.style.opacity = '0.85'; e.target.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={(e) => { e.target.style.opacity = '1'; e.target.style.transform = 'translateY(0)'; }}
          >
            Backup Now
          </button>
        </div>
      )}

      {/* ── Storage Stats Grid ── */}
      {storageStats && (
        <div style={statsGridStyle}>
          {/* Players */}
          <div style={s.statCard('#38bdf8')}>
            <div style={s.statCardGlow('#38bdf8')} />
            <div style={s.statIconRow}>
              <PlayersIcon />
              <span style={s.statLabel}>Players</span>
            </div>
            <div style={s.statNumber('#38bdf8')}>{storageStats.counts.players}</div>
            <div style={s.statSize}>{formatBytes(storageStats.sizes.players)}</div>
            <div style={s.progressBarBg}>
              <div style={s.progressBarFill(
                (storageStats.sizes.players / maxBarSize) * 100, '#38bdf8'
              )} />
            </div>
          </div>

          {/* Plays */}
          <div style={s.statCard('#a78bfa')}>
            <div style={s.statCardGlow('#a78bfa')} />
            <div style={s.statIconRow}>
              <PlaysIcon />
              <span style={s.statLabel}>Plays</span>
            </div>
            <div style={s.statNumber('#a78bfa')}>{storageStats.counts.plays}</div>
            <div style={s.statSize}>{formatBytes(storageStats.sizes.plays)}</div>
            <div style={s.progressBarBg}>
              <div style={s.progressBarFill(
                (storageStats.sizes.plays / maxBarSize) * 100, '#a78bfa'
              )} />
            </div>
          </div>

          {/* Opponents */}
          <div style={s.statCard('#fb923c')}>
            <div style={s.statCardGlow('#fb923c')} />
            <div style={s.statIconRow}>
              <OpponentsIcon />
              <span style={s.statLabel}>Opponents</span>
            </div>
            <div style={s.statNumber('#fb923c')}>{storageStats.counts.opponents}</div>
            <div style={s.statSize}>{formatBytes(storageStats.sizes.opponents)}</div>
            <div style={s.progressBarBg}>
              <div style={s.progressBarFill(
                (storageStats.sizes.opponents / maxBarSize) * 100, '#fb923c'
              )} />
            </div>
          </div>

          {/* Total Storage */}
          <div style={s.statCard('#34d399')}>
            <div style={s.statCardGlow('#34d399')} />
            <div style={s.statIconRow}>
              <StorageIcon />
              <span style={s.statLabel}>Total Storage</span>
            </div>
            <div style={s.statNumber('#34d399')}>{formatBytes(totalSize)}</div>
            <div style={s.statSize}>
              {storageStats.counts.players + storageStats.counts.plays + storageStats.counts.opponents} items
            </div>
            <div style={s.progressBarBg}>
              <div style={s.progressBarFill(100, '#34d399')} />
            </div>
          </div>
        </div>
      )}

      {/* ── Actions Section ── */}
      <div style={actionsGridStyle}>
        {/* Export */}
        <div
          style={s.actionCard}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'rgba(56,189,248,0.3)';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#334155';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <div style={s.actionIconWrap('rgba(56,189,248,0.12)')}>
            <DownloadIcon />
          </div>
          <div style={s.actionTitle}>Export All Data</div>
          <div style={s.actionDesc}>Download JSON backup</div>
          <button
            style={s.btnAccent}
            onClick={handleExportAll}
            onMouseEnter={(e) => { e.target.style.opacity = '0.85'; }}
            onMouseLeave={(e) => { e.target.style.opacity = '1'; }}
          >
            Export
          </button>
        </div>

        {/* Import */}
        <div
          style={s.actionCard}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'rgba(167,139,250,0.3)';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#334155';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <div style={s.actionIconWrap('rgba(167,139,250,0.12)')}>
            <UploadIcon />
          </div>
          <div style={s.actionTitle}>Import Data</div>
          <div style={s.actionDesc}>Restore from backup</div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleImport}
            style={{ display: 'none' }}
          />
          <button
            style={s.btnSecondary}
            onClick={() => fileInputRef.current?.click()}
            onMouseEnter={(e) => { e.target.style.background = 'rgba(167,139,250,0.08)'; }}
            onMouseLeave={(e) => { e.target.style.background = 'transparent'; }}
          >
            Import
          </button>
        </div>

        {/* Clear */}
        <div
          style={s.actionCard}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'rgba(239,68,68,0.3)';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#334155';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <div style={s.actionIconWrap('rgba(239,68,68,0.12)')}>
            <TrashIcon />
          </div>
          <div style={s.actionTitle}>Clear All Data</div>
          <div style={s.actionDesc}>Remove everything</div>
          <button
            style={s.btnDanger}
            onClick={() => setShowClearConfirm(true)}
            onMouseEnter={(e) => { e.target.style.opacity = '0.85'; }}
            onMouseLeave={(e) => { e.target.style.opacity = '1'; }}
          >
            Clear Data
          </button>
        </div>
      </div>

      {/* ── Info Section (collapsible) ── */}
      <div>
        <div
          style={{
            ...s.infoHeader,
            ...(infoExpanded ? { borderRadius: '12px 12px 0 0' } : {}),
          }}
          onClick={() => setInfoExpanded(!infoExpanded)}
        >
          <span style={s.infoTitle}>About IndexedDB Storage</span>
          <ChevronIcon expanded={infoExpanded} />
        </div>
        {infoExpanded && (
          <div style={s.infoBody}>
            <div style={s.infoItem}>
              <span style={s.infoBullet}>&bull;</span>
              <span><span style={s.infoStrong}>IndexedDB:</span> Much more reliable than localStorage -- won't be cleared by cache clearing</span>
            </div>
            <div style={s.infoItem}>
              <span style={s.infoBullet}>&bull;</span>
              <span><span style={s.infoStrong}>Offline-First:</span> Works without internet, data stays on your device</span>
            </div>
            <div style={s.infoItem}>
              <span style={s.infoBullet}>&bull;</span>
              <span><span style={s.infoStrong}>Regular Backups:</span> Export weekly to prevent data loss</span>
            </div>
            <div style={s.infoItem}>
              <span style={s.infoBullet}>&bull;</span>
              <span><span style={s.infoStrong}>Device-Specific:</span> Export/import to transfer between devices</span>
            </div>
            <div style={{ ...s.infoItem, marginBottom: 0 }}>
              <span style={s.infoBullet}>&bull;</span>
              <span><span style={s.infoStrong}>PWA Advantage:</span> Data persists even when app is updated</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Clear Data Confirmation Modal ── */}
      {showClearConfirm && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={s.modalWarning}>
              <WarningIcon />
            </div>
            <div style={s.modalTitle}>Confirm Data Deletion</div>
            <div style={s.modalText}>
              This will permanently delete ALL your data including:
            </div>
            <ul style={s.modalList}>
              {['All players', 'All plays', 'All opponents', 'All statistics', 'All settings'].map((item) => (
                <li key={item} style={s.modalListItem}>
                  <span style={s.modalListBullet}>&bull;</span>
                  {item}
                </li>
              ))}
            </ul>
            <div style={s.modalDanger}>
              This action CANNOT be undone!
            </div>
            <div style={s.modalBtns}>
              <button
                style={s.modalCancel}
                onClick={() => setShowClearConfirm(false)}
                onMouseEnter={(e) => { e.target.style.background = 'rgba(71,85,105,0.3)'; }}
                onMouseLeave={(e) => { e.target.style.background = 'transparent'; }}
              >
                Cancel
              </button>
              <button
                style={s.modalDelete}
                onClick={handleClearData}
                onMouseEnter={(e) => { e.target.style.opacity = '0.85'; }}
                onMouseLeave={(e) => { e.target.style.opacity = '1'; }}
              >
                Delete Everything
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataManagement;
